import { Controller } from "@hotwired/stimulus"

/**
 * 4-step wizard for generating GO4SPILOT reports.
 *
 * Steps:
 *   1. Report type   (card selection)
 *   2. Period & scope (date range + scope select)
 *   3. Content        (checkboxes + card)
 *   4. Format & send  (card selection + summary)
 */
export default class extends Controller {
  static targets = [
    "step",          // each step panel [data-step="1..4"]
    "sidebar",       // sidebar step items
    "progress",      // progress bar fill
    "progressLabel", // "1 / 4 — 25 %"
    "footer",        // footer breadcrumb text
    "prevBtn",       // "Précédent" button
    "submitBtn",     // "Générer le rapport" (step 4 only)
    "nextBtn",       // "Continuer" button
    "summary",       // summary block on step 4
    "summaryText",   // summary description text
    "titleField",    // hidden title input
    "scopeSub",      // conditional school selector for scope
  ]

  static values = {
    current: { type: Number, default: 1 },
    total:   { type: Number, default: 4 },
  }

  connect() {
    this.selections = {
      reportType: null,
      reportTypeLabel: "",
      scopeType: "all",
      sections: [],
      detailLevel: null,
      format: null,
    }

    this._syncInitialSelections()
    this._render()
  }

  _syncInitialSelections() {
    // Report type (pre-selected from index category cards)
    const selectedType = this.element.querySelector(
      '[data-card-group="reportType"] .g4sp-wf-card--selected'
    )
    if (selectedType) {
      this.selections.reportType = selectedType.dataset.value
      this.selections.reportTypeLabel =
        selectedType.querySelector("[data-label]")?.textContent?.trim() || ""
    }

    // Scope type
    const scopeSelect = this.element.querySelector(
      'select[name="pilot_report[scope_type]"]'
    )
    if (scopeSelect && scopeSelect.value) {
      this.selections.scopeType = scopeSelect.value
    }

    // Sections (pre-checked checkboxes)
    this.element
      .querySelectorAll(".g4sp-wf-checklist input:checked")
      .forEach((cb) => {
        this.selections.sections.push(cb.value)
      })

    // Detail level
    const selectedDetail = this.element.querySelector(
      '[data-card-group="detailLevel"] .g4sp-wf-card--selected'
    )
    if (selectedDetail) {
      this.selections.detailLevel = selectedDetail.dataset.value
    }

    // Format (default: pdf)
    const selectedFormat = this.element.querySelector(
      '[data-card-group="format"] .g4sp-wf-card--selected'
    )
    if (selectedFormat) {
      this.selections.format = selectedFormat.dataset.value
    }

    this._toggleScopeSub()
    this._autoGenerateTitle()
  }

  // ──── Navigation ────
  next() {
    if (!this._validateStep(this.currentValue)) return
    if (this.currentValue < this.totalValue) {
      this.currentValue++
      this._render()
    }
  }

  prev() {
    if (this.currentValue > 1) {
      this.currentValue--
      this._render()
    }
  }

  goToStep(event) {
    const target = parseInt(event.currentTarget.dataset.step, 10)
    if (target < this.currentValue) {
      this.currentValue = target
      this._render()
    }
  }

  // ──── Card Selection ────
  selectCard(event) {
    const card = event.currentTarget
    const group = card.dataset.group
    const value = card.dataset.value

    // Deselect siblings
    const container = card.closest("[data-card-group]")
    if (container) {
      container.querySelectorAll("[data-group]").forEach((c) => {
        c.classList.remove("g4sp-wf-card--selected")
        const check = c.querySelector(".g4sp-wf-card-check")
        if (check) check.style.display = "none"
      })
    }

    card.classList.add("g4sp-wf-card--selected")
    const check = card.querySelector(".g4sp-wf-card-check")
    if (check) check.style.display = ""

    if (group === "reportType") {
      this.selections.reportType = value
      this.selections.reportTypeLabel =
        card.querySelector("[data-label]")?.textContent?.trim() || value
      const input = this.element.querySelector(
        'input[name="pilot_report[report_type]"]'
      )
      if (input) input.value = value
    } else if (group === "detailLevel") {
      this.selections.detailLevel = value
    } else if (group === "format") {
      this.selections.format = value
      const input = this.element.querySelector(
        'input[name="pilot_report[format]"]'
      )
      if (input) input.value = value
    }

    this._updateSummary()
    this._autoGenerateTitle()
  }

  // ──── Scope Type Change ────
  changeScopeType(event) {
    this.selections.scopeType = event.currentTarget.value
    this._toggleScopeSub()
    this._updateSummary()
  }

  toggleSection(event) {
    const cb = event.currentTarget
    const val = cb.value
    if (cb.checked) {
      if (!this.selections.sections.includes(val)) {
        this.selections.sections.push(val)
      }
    } else {
      this.selections.sections = this.selections.sections.filter(
        (s) => s !== val
      )
    }
    this._updateSummary()
  }

  // ──── Private ────
  _toggleScopeSub() {
    const show = this.selections.scopeType === "specific"
    this.scopeSubTargets.forEach((el) => {
      el.style.display = show ? "" : "none"
      if (show) this._refreshSlimSelects(el)
    })
  }

  _refreshSlimSelects(container) {
    if (!window.SlimSelect) return
    container.querySelectorAll("select").forEach((sel) => {
      if (sel.slim) return
      if (typeof window.initSlimSelects === "function") {
        window.initSlimSelects(`select[name="${sel.name}"]`)
      }
    })
  }

  _validateStep(step) {
    switch (step) {
      case 1:
        if (!this.selections.reportType) {
          this._shake(
            this.element.querySelector('[data-card-group="reportType"]')
          )
          return false
        }
        return true
      case 2:
        return true
      case 3:
        return true
      case 4:
        return true
      default:
        return true
    }
  }

  disconnect() {
    if (this._shakeTimeout) {
      clearTimeout(this._shakeTimeout)
      this._shakeTimeout = null
    }
  }

  _shake(el) {
    if (!el) return
    el.classList.add("g4sp-wf-shake")
    this._shakeTimeout = setTimeout(
      () => el.classList.remove("g4sp-wf-shake"),
      600
    )
  }

  _render() {
    const step = this.currentValue
    const pct = Math.round((step / this.totalValue) * 100)

    // Show/hide step panels
    this.stepTargets.forEach((panel) => {
      const s = parseInt(panel.dataset.step, 10)
      panel.style.display = s === step ? "" : "none"
    })

    // Update sidebar
    this.sidebarTargets.forEach((item) => {
      const s = parseInt(item.dataset.step, 10)
      item.classList.remove(
        "g4sp-wf-step--active",
        "g4sp-wf-step--done",
        "g4sp-wf-step--disabled"
      )
      if (s === step) {
        item.classList.add("g4sp-wf-step--active")
      } else if (s < step) {
        item.classList.add("g4sp-wf-step--done")
      } else {
        item.classList.add("g4sp-wf-step--disabled")
      }
    })

    // Progress bar
    if (this.hasProgressTarget) {
      this.progressTarget.style.width = pct + "%"
    }
    if (this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent = `${step} / ${this.totalValue} — ${pct} %`
    }

    // Footer text
    if (this.hasFooterTarget) {
      const active = this.sidebarTargets.find(
        (i) => parseInt(i.dataset.step, 10) === step
      )
      if (active) {
        const title = active.querySelector("[data-step-title]")?.textContent || ""
        const sub = active.querySelector("[data-step-sub]")?.textContent || ""
        this.footerTarget.textContent = ''
        const strong = document.createElement('strong')
        strong.textContent = title
        this.footerTarget.appendChild(strong)
        const span = document.createElement('span')
        span.textContent = ` — ${sub}`
        this.footerTarget.appendChild(span)
      }
    }

    // Prev/Next/Submit visibility
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.style.display = step > 1 ? "" : "none"
    }
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.style.display = step < this.totalValue ? "" : "none"
    }
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.style.display =
        step === this.totalValue ? "" : "none"
    }

    // Summary block on step 4
    if (this.hasSummaryTarget) {
      this.summaryTarget.style.display = step === this.totalValue ? "" : "none"
    }

    this._updateSummary()
  }

  _updateSummary() {
    if (!this.hasSummaryTextTarget) return

    const parts = []
    if (this.selections.reportTypeLabel) {
      parts.push(this.selections.reportTypeLabel)
    }

    // Period dates
    const startDate = this.element.querySelector(
      'input[name="pilot_report[start_date]"]'
    )
    const endDate = this.element.querySelector(
      'input[name="pilot_report[end_date]"]'
    )
    if (startDate?.value && endDate?.value) {
      parts.push(`${startDate.value} → ${endDate.value}`)
    }

    if (this.selections.scopeType === "specific") {
      const schoolSelect = this.element.querySelector(
        'select[name="pilot_report[school_ids][]"]'
      )
      if (schoolSelect) {
        const count = schoolSelect.selectedOptions.length
        if (count > 0) parts.push(`${count} établissement(s)`)
      }
    }

    const sectionCount = this.selections.sections.length
    if (sectionCount > 0) {
      parts.push(`${sectionCount} section(s)`)
    }
    const fmt = this.selections.format || "pdf"
    parts.push(`format ${fmt.toUpperCase()}`)

    this.summaryTextTarget.textContent = parts.join(" · ")
  }

  _autoGenerateTitle() {
    if (!this.hasTitleFieldTarget) return

    const parts = []
    if (this.selections.reportTypeLabel) {
      parts.push(this.selections.reportTypeLabel)
    }

    const startDate = this.element.querySelector(
      'input[name="pilot_report[start_date]"]'
    )
    if (startDate?.value) {
      parts.push(startDate.value)
    }

    if (parts.length > 0) {
      this.titleFieldTarget.value = parts.join(" — ")
    }
  }
}
